import { Model } from 'app/model/backendmodel';
import { IDataFlowSink, FLOW } from 'app/model/adapters/dataflow/IDataflow';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Metamodel } from 'app/model/base/model';
import { Dataset } from 'app/model/dataset/dataset';
import { DataFlowSink, FileNameOption } from 'app/model/dataflow/dataflow-sink';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { DataFlowSchemaAdapterUtils } from './dataflow-schema-adapter-utils';
import {
    TransformationAdapter,
    IDslScriptable
} from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { AzureSqlTableDataset } from 'app/model/dataset/azure-sql-table.dataset';
import { AzureSqlDWTableDataset } from 'app/model/dataset/azure-sql-dw-table.dataset';
import { DataFlowTypeConvertor } from 'app/model/dataflow/data-flow-type-convertor';
import { GlobalVariables } from 'app/services/globals';
import {
    AzureBlobFSLinkedService,
    AzureDataExplorerDataset,
    AzureSqlDWLinkedService,
    BaseAsyncAdapter,
    DataWarehouseLinkedService,
    DelimitedTextDataset,
    ITridentResource,
    LakehouseLinkedService,
    LakehouseTableDataset,
    ParquetDataset,
    SnowflakeTableDataset
} from 'shell-models-lib';
import { Owned } from 'app/model/base/named';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import {
    IntegerDataFlowType,
    BooleanDataFlowType,
    StringDataFlowType,
    ArrayDataFlowType
} from 'app/model/dataflow/dataflow-datatype';
import { DataFlowExpressionUtils } from './expression.util';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { AzureCosmosDbV2Dataset } from 'app/model/dataset/azure-cosmos-db.v2.dataset';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { DataFlowAdapterUtils } from './adapter.util';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { SqlBaseSettings } from 'app/model/dataflow/dataset-settings/sql-base-settings';
import { AzurePostgreSqlTableDataset } from 'app/model/dataset/azure-postgre-sql-table.dataset';
import { CassandraDataset } from 'app/model/dataset/cassandra.dataset';
import { SnowflakeSettings } from 'app/model/dataflow/dataset-settings/snowflake-settings';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { AzureMySqlTableDataset } from 'app/model/dataset/azure-mysql-table.dataset';
import { AzureDataLakeStoreLinkedService } from 'app/model/linkedservice/azure-data-lake-store.linkedservice';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { AzureSqlDatabaseLinkedService } from 'app/model/linkedservice/azure-sql.linkedservice';
import { DatasetUtils } from 'app/model/dataset/dataset.utils';
import { LinkedServiceUtils } from 'app/model/linkedservice/linked-service.utils';
import { DynamicsEntityDataset } from 'app/model/dataset/dynamics-entity.dataset';
import { DynamicsCrmEntityDataset } from 'app/model/dataset/dynamics-crm.dataset';
import { CommonDataServiceEntityDataset } from 'app/model/dataset/common-data-service.dataset';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DataFlowSink),
    asynchronous: true
})
export class SinkAdapter extends BaseAsyncAdapter<DataFlowSink, IDataFlowSink> implements IDslScriptable {
    private _isCombinedScriptEnabled: boolean = !GlobalVariables.splitScriptEnabled;

    public async getDslScript(model: DataFlowSink): Promise<string> {
        const inputStream = model.inputStreams.values().next().value;
        return `${inputStream ? inputStream.qualifiedName : ''} ${await this.translateSinkPropertiesToDsl(
            model
        )} ${FLOW} ${model.name}`;
    }

    public async translateSinkPropertiesToDsl(model: DataFlowSink): Promise<string> {
        const props: string[] = [];
        if (!model.isCacheSink || model.hasSchemaDriftAttribute) {
            props.push(
                `allowSchemaDrift: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    model.allowSchemaDrift,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        props.push(
            `validateSchema: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                model.validateSchema,
                BooleanDataFlowType.TYPE
            )}`
        );

        if (model.isWorkspaceDatabase) {
            props.push(`store: 'synapse'`);
            props.push(
                `databaseType: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceDatabaseType)}`
            );
            props.push(`format: 'table'`);
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceDatabase)}`);
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.workspaceTable)}`);
            if (model.tableActionOption === TableActionOption.Recreate) {
                props.push('recreate:true');
            }
            if (model.inlineDataSetColumns.length > 0) {
                const columnsFromDsl = DataFlowSchemaAdapterUtils.getInputDefinitionDslForColumns(
                    model.inlineDataSetColumns
                );
                props.push(columnsFromDsl);
            }
        } else {
            if (model.sinkDataset) {
                const datasetColumns = DataFlowTypeConvertor.convertDatasetColumnsToDataFlowColumns(
                    model.sinkDataset,
                    model,
                    { loggerService: await getServiceInstance(this.context.contextData, 'LoggerService') }
                );
                const columnsFromDsl = DataFlowSchemaAdapterUtils.getInputDefinitionDslForColumns(datasetColumns);
                props.push(columnsFromDsl);

                const sinkLinkedService = model.sinkDataset.linkedService;
                const supportSpecifyingTmpDB =
                    !!sinkLinkedService &&
                    (
                        await getServiceInstance<LinkedServiceRegistryService>(
                            this.context.contextData,
                            'LinkedServiceRegistryService'
                        )
                    ).getLinkedServiceConfiguration(Metamodel.modelOf(sinkLinkedService).name)
                        .dataflowSinkStagingSchema;
                if (supportSpecifyingTmpDB && !model.useTmpDB && model.stagingSchemaName.expression) {
                    props.push(
                        `stagingSchemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.stagingSchemaName
                        )}`
                    );
                }
            }

            if (
                (model.isInlineDataset && model.datasetSettings instanceof DelimitedTextSettings) ||
                (!model.isInlineDataset && model.sinkDataset instanceof DelimitedTextDataset)
            ) {
                if (model.emptyLinesAsHeader.expression && model.emptyLinesAsHeader.expression !== '0') {
                    props.push(
                        `emptyLinesAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.emptyLinesAsHeader,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
            }

            if (model.sinkLinkedService && model.datasetSettings) {
                const adapter = this.context.dataflowDSLAdapterContext.adapterFor(
                    Metamodel.modelOf(model.datasetSettings)
                );

                if (!!adapter) {
                    if (model.inlineDataSetColumns.length > 0) {
                        const columnsFromDsl = DataFlowSchemaAdapterUtils.getInputDefinitionDslForColumns(
                            model.inlineDataSetColumns
                        );
                        props.push(columnsFromDsl);
                    }
                    const settingProps = await adapter.toDSL(model.datasetSettings, false);
                    props.push(...settingProps);
                }
            }

            const supportTableOption =
                DatasetUtils.isSqlBasedDataflowDataset(model.sinkDataset) ||
                model.sinkDataset instanceof AzureCosmosDbV2Dataset ||
                model.sinkDataset instanceof SnowflakeTableDataset ||
                model.sinkDataset instanceof AzurePostgreSqlTableDataset ||
                model.sinkDataset instanceof AzureMySqlTableDataset ||
                model.sinkDataset instanceof LakehouseTableDataset;
            if (
                supportTableOption ||
                model.sinkDataset instanceof RestResourceDataset ||
                model.sinkDataset instanceof DynamicsEntityDataset ||
                model.sinkDataset instanceof DynamicsCrmEntityDataset ||
                model.sinkDataset instanceof CommonDataServiceEntityDataset
            ) {
                props.push(
                    `deletable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.allowDelete,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                props.push(
                    `insertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.allowInsert,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                props.push(
                    `updateable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.allowUpdate,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                props.push(
                    `upsertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.allowUpsert,
                        BooleanDataFlowType.TYPE
                    )}`
                );

                if (
                    model.sinkDataset instanceof DynamicsEntityDataset ||
                    model.sinkDataset instanceof DynamicsCrmEntityDataset ||
                    model.sinkDataset instanceof CommonDataServiceEntityDataset
                ) {
                    if (isTruthy(model.allowDelete) || isTruthy(model.allowUpdate) || isTruthy(model.allowUpsert)) {
                        if (model.alternateKeyName && model.alternateKeyName.expression) {
                            props.push(
                                `alternateKeyName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.alternateKeyName
                                )}`
                            );
                        }
                    }
                    if (model.timeout.expression) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                } else if (model.sinkDataset instanceof RestResourceDataset) {
                    if (
                        isTruthy(model.allowDelete) ||
                        isTruthy(model.allowInsert) ||
                        isTruthy(model.allowUpdate) ||
                        isTruthy(model.allowUpsert)
                    ) {
                        if (model.rowRelativeUrl && model.rowRelativeUrl.expression) {
                            props.push(
                                `rowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.rowRelativeUrl
                                )}`
                            );
                        }
                        if (isTruthy(model.skipRowRelativeUrl)) {
                            props.push(
                                `skipRowRelativeUrl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.skipRowRelativeUrl,
                                    BooleanDataFlowType.TYPE
                                )}`
                            );
                        }
                    }
                } else {
                    if (model.requiresKeyColumns && !model.isInlineDataset) {
                        const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(model);
                        if (keyColumns) {
                            props.push(keyColumns);
                        }
                        if (isTruthy(model.skipKeyWrites) && !(model.sinkDataset instanceof SnowflakeTableDataset)) {
                            props.push(
                                `skipKeyWrites:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.skipKeyWrites,
                                    BooleanDataFlowType.TYPE
                                )}`
                            );
                        }
                    }
                }
            }

            if (model.sinkDataset instanceof LakehouseTableDataset) {
                if (model.optimizedWrite && model.optimizedWrite.expression) {
                    props.push(
                        `optimizedWrite: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.optimizedWrite,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }

                if (model.mergeSchema && model.mergeSchema.expression) {
                    props.push(
                        `mergeSchema: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.mergeSchema,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }

                if (model.autoCompact && model.autoCompact.expression) {
                    props.push(
                        `autoCompact: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.autoCompact,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }
            }

            if (supportTableOption || model.sinkDataset instanceof AzureDataExplorerDataset) {
                if (model.tableActionOption === TableActionOption.Recreate) {
                    props.push('recreate:true');
                }

                if (model.tableActionOption === TableActionOption.Truncate) {
                    props.push(`truncate:true`);
                }
            }

            if (model.sinkDataset instanceof ParquetDataset) {
                props.push(`format: 'parquet'`);
            }

            if (model.sinkDataset instanceof RestResourceDataset) {
                if (model.allowInsert.expression && model.insertHttpMethod.expression) {
                    props.push(
                        `insertHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.insertHttpMethod)}`
                    );
                }
                if (model.allowDelete.expression && model.deleteHttpMethod.expression) {
                    props.push(
                        `deleteHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.deleteHttpMethod)}`
                    );
                }
                if (model.allowUpsert.expression && model.upsertHttpMethod.expression) {
                    props.push(
                        `upsertHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.upsertHttpMethod)}`
                    );
                }
                if (model.allowUpdate.expression && model.updateHttpMethod.expression) {
                    props.push(
                        `updateHttpMethod: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.updateHttpMethod)}`
                    );
                }
                if (model.requestTimeout.expression) {
                    props.push(
                        `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.requestTimeout,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (model.requestInterval.expression) {
                    props.push(
                        `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.requestInterval,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (model.restDataFormat && model.batchSize.expression) {
                    props.push(
                        `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.batchSize,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (model.httpCompressionType.expression) {
                    props.push(
                        `httpCompressionType: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.httpCompressionType
                        )}`
                    );
                }
                if (model.additionalHeaders && model.additionalHeaders.length) {
                    const headers = model.additionalHeaders
                        .filter((header) => header.name && header.value)
                        .map(
                            (header) =>
                                `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    header.paramName as DataFlowExpression
                                )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    header.paramValue as DataFlowExpression
                                )}`
                        )
                        .join(', ');
                    if (headers) {
                        props.push(`headers: [${headers}]`);
                    }
                }
                if (model.queryParameters && model.queryParameters.length) {
                    const queryParameters = model.queryParameters
                        .filter((p) => p.name && p.value)
                        .map(
                            (p) =>
                                `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    p.paramName as DataFlowExpression
                                )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    p.paramValue as DataFlowExpression
                                )}`
                        )
                        .join(', ');
                    if (queryParameters) {
                        props.push(`queryParameters: [${queryParameters}]`);
                    }
                }
                if (model.restDataFormat === 'json') {
                    const formatSettings = [`'type' -> 'json'`];
                    if (model.dateFormat) {
                        formatSettings.push(`'outputDateFormat' -> '${model.dateFormat}'`);
                    }
                    if (model.timestampFormat) {
                        formatSettings.push(`'outputTimestampFormat' -> '${model.timestampFormat}'`);
                    }
                    const formatSettingsInDsl = formatSettings.join(', ');
                    if (formatSettingsInDsl) {
                        props.push(`requestFormat: [${formatSettingsInDsl}]`);
                    }
                }
            }

            if (
                DatasetUtils.isSqlBasedDataflowDataset(model.sinkDataset) ||
                model.sinkDataset instanceof AzurePostgreSqlTableDataset ||
                model.sinkDataset instanceof AzureMySqlTableDataset ||
                model.sinkDataset instanceof AzureDataExplorerDataset
            ) {
                props.push(`format: 'table'`);
                if (model.sinkDataset instanceof AzureSqlDWTableDataset) {
                    props.push(`staged: ${model.staged}`);
                    if (model.staged && model.useCopyCommandForStaging) {
                        props.push(`allowCopyCommand: true`);
                    }
                }
                if (model.sinkDataset instanceof AzureDataExplorerDataset) {
                    if (!stringIsNullOrEmpty(model.timeout.expression)) {
                        props.push(
                            `timeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.timeout,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                }
                if (model.batchSize.expression) {
                    props.push(
                        `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.batchSize,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                const modelPreSQLScripts = model.preSQLScript as DataFlowExpression[];
                const modelPostSQLScripts = model.postSQLScript as DataFlowExpression[];
                if (model.isListOfPreSQLScripts) {
                    const preSQLScripts = modelPreSQLScripts
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    if (!!preSQLScripts) {
                        props.push(`preSQLs:[${preSQLScripts}]`);
                    }
                } else {
                    if (modelPreSQLScripts[0].expression) {
                        props.push(
                            `preSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                modelPreSQLScripts[0],
                                ArrayDataFlowType.TYPE
                            )}`
                        );
                    }
                }
                if (model.isListOfPostSQLScripts) {
                    const postSQLScripts = modelPostSQLScripts
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    if (!!postSQLScripts) {
                        props.push(`postSQLs:[${postSQLScripts}]`);
                    }
                } else {
                    if (modelPostSQLScripts[0].expression) {
                        props.push(
                            `postSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                modelPostSQLScripts[0],
                                ArrayDataFlowType.TYPE
                            )}`
                        );
                    }
                }
            } else if (model.sinkDataset instanceof AzureCosmosDbV2Dataset) {
                props.push(`format: 'document'`);
                if (!stringIsNullOrEmpty(model.batchSize.expression)) {
                    props.push(
                        `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.batchSize,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (!stringIsNullOrEmpty(model.partitionKey.expression)) {
                    props.push(
                        `partitionKey: [${DataFlowExpressionUtils.wrapExpressionConfigValue(model.partitionKey)}]`
                    );
                }
                if (!stringIsNullOrEmpty(model.throughput.expression)) {
                    props.push(
                        `throughput: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.throughput,
                            IntegerDataFlowType.TYPE
                        )}`
                    );
                }
                if (!stringIsNullOrEmpty(model.writeThroughPutBudget.expression)) {
                    if (model.cosmosUseTotalWriteThroughputBudget) {
                        props.push(
                            `totalWriteThroughputBudget: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.writeThroughPutBudget,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    } else {
                        props.push(
                            `writeThroughPutBudget: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.writeThroughPutBudget,
                                IntegerDataFlowType.TYPE
                            )}`
                        );
                    }
                }
            } else if (model.sinkDataset instanceof SnowflakeTableDataset) {
                props.push(`format: 'table'`);
            } else if (model.datasetSettings && model.datasetSettings instanceof SqlBaseSettings) {
                // Skip adding these, handled in the settings adapter
            } else {
                let fileNameOption: string;
                switch (model.fileNameOption) {
                    case FileNameOption.Default:
                        break;
                    case FileNameOption.FilePattern:
                        fileNameOption = `filePattern:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.filePattern
                        )}`;
                        break;
                    case FileNameOption.PartitionFileNames:
                        fileNameOption = `partitionFileNames:[${model.partitionFileNames
                            .map((name) => DataFlowExpressionUtils.wrapExpressionConfigValue(name))
                            .join(',')}]`;
                        break;
                    case FileNameOption.RowUrlColumn:
                        fileNameOption = `rowUrlColumn:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.rowUrl,
                            StringDataFlowType.TYPE,
                            true
                        )}`;
                        break;
                    case FileNameOption.RowFolderUrlColumn:
                        fileNameOption = `rowFolderUrlColumn:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.rowFolderUrlColumn,
                            StringDataFlowType.TYPE,
                            true
                        )}`;
                        break;
                    case FileNameOption.SingleFile:
                        fileNameOption = `partitionFileNames:[${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.singleFile
                        )}]`;
                        break;
                    default:
                        break;
                }
                props.push(fileNameOption);

                if (isTruthy(model.clearFolder) && !model.isInlineDataset) {
                    props.push(
                        `truncate: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.clearFolder,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }

                const sinkLinkedService =
                    model.sinkLinkedService || (model.sinkDataset && model.sinkDataset.linkedService);
                if (
                    sinkLinkedService instanceof AzureDataLakeStoreLinkedService ||
                    sinkLinkedService instanceof AzureBlobFSLinkedService
                ) {
                    if (!model.umask.templateExpression) {
                        props.push(`umask: 0${model.umask.expression}`);
                    } else {
                        props.push(`umask: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.umask)}`);
                    }
                }

                if (
                    sinkLinkedService instanceof AzureBlobFSLinkedService ||
                    (model.isInlineDataset && model.sinkLinkedService instanceof AzureBlobFSLinkedService)
                ) {
                    const modelPreFileCommands = model.preFileCommand as DataFlowExpression[];
                    const preFileCommands = modelPreFileCommands
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    props.push(`preCommands: [${preFileCommands}]`);

                    const modelPostFileCommands = model.postFileCommand as DataFlowExpression[];
                    const postFileCommands = modelPostFileCommands
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    props.push(`postCommands: [${postFileCommands}]`);
                }
            }

            if (model.dataTypeSettings.dateFormat) {
                props.push(`dateFormat:'${model.dataTypeSettings.dateFormat}'`);
            }
            if (model.dataTypeSettings.timestampFormat) {
                props.push(
                    `timestampFormat:'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(
                        model.dataTypeSettings.timestampFormat
                    )}'`
                );
            }
            if (model.dataTypeSettings.booleanTrueFormat && model.dataTypeSettings.booleanFalseFormat) {
                props.push(
                    `booleanFormat: ['${model.dataTypeSettings.booleanTrueFormat}', '${model.dataTypeSettings.booleanFalseFormat}']`
                );
            }

            if (isTruthy(model.skipDuplicateMapInputs)) {
                props.push(
                    `skipDuplicateMapInputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.skipDuplicateMapInputs,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }

            if (isTruthy(model.skipDuplicateMapOutputs)) {
                props.push(
                    `skipDuplicateMapOutputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.skipDuplicateMapOutputs,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }

            if (isTruthy(model.quoteAll)) {
                props.push(
                    `quoteAll: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.quoteAll,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }

            if (model.delimitedTextHeader && model.delimitedTextHeader.expression) {
                props.push(
                    `header: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.delimitedTextHeader,
                        ArrayDataFlowType.TYPE
                    )}`
                );
            }

            if (model.isCacheSink) {
                const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(model);
                if (keyColumns) {
                    props.push(keyColumns);
                }
                props.push(`store: 'cache'`);
                props.push(`format: 'inline'`);
                props.push(
                    `output: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.cacheSinkOutput,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }

            if (!stringIsNullOrEmpty(model.saveOrder.expression)) {
                props.push(
                    `saveOrder: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.saveOrder,
                        IntegerDataFlowType.TYPE
                    )}`
                );
            }

            if (
                model.sinkDataset instanceof AzureSqlTableDataset ||
                (model.isInlineDataset && model.sinkLinkedService instanceof AzureSqlDatabaseLinkedService) ||
                model.sinkDataset instanceof AzureSqlDWTableDataset ||
                (model.isInlineDataset && model.sinkLinkedService instanceof AzureSqlDWLinkedService)
            ) {
                props.push(
                    `errorHandlingOption: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.enableErrorHandling,
                        StringDataFlowType.TYPE
                    )}`
                );

                const addSqlErrorOutput = () => {
                    if (LinkedServiceUtils.isFileSystemTypeInBackend(model.faultToleranceLinkedService)) {
                        if (!stringIsNullOrEmpty(model.sqlErrorFileSystem.expression)) {
                            props.push(
                                `rejectedData_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.sqlErrorFileSystem
                                )}`
                            );
                        } else if (!stringIsNullOrEmpty(model.dataflow.sqlErrorFileSystem.expression)) {
                            props.push(
                                `rejectedData_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.dataflow.sqlErrorFileSystem
                                )}`
                            );
                        }
                    } else {
                        if (!stringIsNullOrEmpty(model.sqlErrorContainer.expression)) {
                            props.push(
                                `rejectedData_container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.sqlErrorContainer
                                )}`
                            );
                        } else if (!stringIsNullOrEmpty(model.dataflow.sqlErrorContainer.expression)) {
                            props.push(
                                `rejectedData_container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                    model.dataflow.sqlErrorContainer
                                )}`
                            );
                        }
                    }

                    if (!stringIsNullOrEmpty(model.sqlErrorFolderPath.expression)) {
                        props.push(
                            `rejectedData_folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.sqlErrorFolderPath
                            )}`
                        );
                    } else if (!stringIsNullOrEmpty(model.dataflow.sqlErrorFolderPath.expression)) {
                        props.push(
                            `rejectedData_folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.dataflow.sqlErrorFolderPath
                            )}`
                        );
                    }
                };

                if (isTruthy(model.outputRejectedData)) {
                    props.push(
                        `outputRejectedData: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.outputRejectedData,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                    addSqlErrorOutput();
                }

                if (
                    !stringIsNullOrEmpty(model.dataVisibility.expression) &&
                    model.enableErrorHandling.expression !== 'stopOnFirstError'
                ) {
                    props.push(
                        `transactionCommit: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.dataVisibility)}`
                    );
                }

                if (model.enableErrorHandling.expression !== 'stopOnFirstError') {
                    props.push(
                        `reportSuccessOnError: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.reportSuccessOnError,
                            BooleanDataFlowType.TYPE
                        )}`
                    );
                }
            }

            if (
                (model.sinkDataset instanceof SnowflakeTableDataset ||
                    model.datasetSettings instanceof SnowflakeSettings) &&
                model.snowflakeStageInsert
            ) {
                props.push(`stageInsert: true`);
            }

            if (!model.isInlineDataset && model.sinkDataset instanceof CassandraDataset) {
                props.push(`format: 'cassandratable'`);
                props.push(
                    `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.batchSize,
                        IntegerDataFlowType.TYPE
                    )}`
                );
                props.push(
                    `ttl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(model.ttl, IntegerDataFlowType.TYPE)}`
                );
                props.push(`consistencyLevel: '${model.consistencyLevel}'`);
                const modelPreSQLScripts = model.preSQLScript as DataFlowExpression[];
                const modelPostSQLScripts = model.postSQLScript as DataFlowExpression[];
                if (model.isListOfPreSQLScripts) {
                    const preSQLScripts = modelPreSQLScripts
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    if (!!preSQLScripts) {
                        props.push(`preSQLs:[${preSQLScripts}]`);
                    }
                } else {
                    if (modelPreSQLScripts[0].expression) {
                        props.push(
                            `preSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                modelPreSQLScripts[0],
                                ArrayDataFlowType.TYPE
                            )}`
                        );
                    }
                }
                if (model.isListOfPostSQLScripts) {
                    const postSQLScripts = modelPostSQLScripts
                        .filter((script) => !!script.expression)
                        .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                        .join(',');
                    if (!!postSQLScripts) {
                        props.push(`postSQLs:[${postSQLScripts}]`);
                    }
                } else {
                    if (modelPostSQLScripts[0].expression) {
                        props.push(
                            `postSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                modelPostSQLScripts[0],
                                ArrayDataFlowType.TYPE
                            )}`
                        );
                    }
                }

                if (model.requiresKeyColumns) {
                    const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(model);
                    if (keyColumns) {
                        props.push(keyColumns);
                    }
                }
            }

            const addAssertFailureOutput = () => {
                if (LinkedServiceUtils.isFileSystemTypeInBackend(model.faultToleranceLinkedService)) {
                    if (!stringIsNullOrEmpty(model.assertFailureFileSystem.expression)) {
                        props.push(
                            `assertFailure_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.assertFailureFileSystem
                            )}`
                        );
                    } else if (!stringIsNullOrEmpty(model.dataflow.assertFailureFileSystem.expression)) {
                        props.push(
                            `assertFailure_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.dataflow.assertFailureFileSystem
                            )}`
                        );
                    }
                } else {
                    if (!stringIsNullOrEmpty(model.assertFailureContainer.expression)) {
                        props.push(
                            `assertFailure_container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.assertFailureContainer
                            )}`
                        );
                    } else if (!stringIsNullOrEmpty(model.dataflow.assertFailureContainer.expression)) {
                        props.push(
                            `assertFailure_container: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                model.dataflow.assertFailureContainer
                            )}`
                        );
                    }
                }

                if (!stringIsNullOrEmpty(model.assertFailureFolderPath.expression)) {
                    props.push(
                        `assertFailure_folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.assertFailureFolderPath
                        )}`
                    );
                } else if (!stringIsNullOrEmpty(model.dataflow.assertFailureFolderPath.expression)) {
                    props.push(
                        `assertFailure_folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            model.dataflow.assertFailureFolderPath
                        )}`
                    );
                }
            };

            if (isTruthy(model.outputAssertFailedRowsToAltStorage)) {
                props.push(
                    `outputAssertFailedRows: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.outputAssertFailedRowsToAltStorage,
                        BooleanDataFlowType.TYPE
                    )}`
                );
                addAssertFailureOutput();
            }

            if (isTruthy(model.ignoreAssertFailedRows) && model.hasAssertUpstream) {
                props.push(
                    `ignoreAssertFailedRows: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        model.ignoreAssertFailedRows,
                        BooleanDataFlowType.TYPE
                    )}`
                );
            }
        }
        const columns = DataFlowSchemaAdapterUtils.getColumnsDsl(model);
        const partition = TransformationAdapter.getPartitionDslScript(model);

        if (model.schema.length > 0) {
            props.push(columns);
        }

        if (partition) {
            props.push(partition);
        }

        // Fabric migration: supplement format properties from sink
        if (this.context.adapterConfig.convertToFabric) {
            const dataset = model.sinkDataset;
            const linkedService = model.isInlineDataset ? model.sinkLinkedService : dataset?.linkedService;
            const adapter = dataset
                ? DataFlowAdapterUtils.getFormatAdapterForDataset(this.context, dataset)
                : (model.datasetSettings ? this.context.dataflowDSLAdapterContext.adapterFor(Metamodel.modelOf(model.datasetSettings)) : undefined);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (adapter as any)?.supplementPropsForFabricMigration?.(dataset, linkedService, props, false, model.isInlineDataset, model.datasetSettings);
        }

        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = DataFlowAdapterUtils.getUnknownProps(model, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');
        return `sink(${transformationData})`;
    }

    protected async _toResource(model: DataFlowSink): Promise<IDataFlowSink> {
        const resource: IDataFlowSink = {
            dataset: model.sinkDataset
                ? (ReferenceAdapter.toResource(model.sinkDataset, this.context) as Model.DatasetReference)
                : undefined,
            linkedService:
                model.isInlineDataset && model.sinkLinkedService
                    ? (ReferenceAdapter.toResource(
                          model.sinkLinkedService,
                          this.context
                      ) as Model.LinkedServiceReference)
                    : undefined,
            schemaLinkedService:
                model.isInlineDataset && model.schemaLinkedService && model.includeSchemaLinkedService
                    ? (ReferenceAdapter.toResource(
                          model.schemaLinkedService,
                          this.context
                      ) as Model.LinkedServiceReference)
                    : undefined,
            name: model.name,
            description: model.description,
            maxConcurrentConnections: model.maxConcurrentConnections
        };

        if (model.faultToleranceLinkedService) {
            resource.rejectedDataLinkedService = ReferenceAdapter.toResource(
                model.faultToleranceLinkedService,
                this.context
            ) as Model.LinkedServiceReference;
        }

        if (__IS_FABRIC_MDF__ && this._isMdfFabricArtifactSink(model)) {
            await this._applyMdfArtifactSinkShape(model, resource);
        }

        if (!this._isCombinedScriptEnabled) {
            resource.script = await this.getDslScript(model);
        }

        return resource;
    }

    protected async _toModel(resource: IDataFlowSink, dataflowSink?: DataFlowSink): Promise<DataFlowSink> {
        if (!dataflowSink) {
            dataflowSink = Metamodel.modelFor(DataFlowSink).create<DataFlowSink>(resource.name);
        } else {
            dataflowSink.name = resource.name;
        }
        // set snowflakeStageInsert and allowCopyCommand to false for all dataflow sinks loaded from backend
        // their value will be set to true if there are 'stageInsert: true' and 'allowCopyCommand: true' in dsl
        dataflowSink.snowflakeStageInsert = false;
        dataflowSink.useCopyCommandForStaging = false;
        dataflowSink.restDataFormat = undefined;
        dataflowSink.insertHttpMethod.expression = '';
        dataflowSink.upsertHttpMethod.expression = '';
        dataflowSink.updateHttpMethod.expression = '';
        dataflowSink.deleteHttpMethod.expression = '';
        if (resource.dataset) {
            dataflowSink.sinkDataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                resource.dataset, this.context
            );
        }
        if (resource.linkedService) {
            dataflowSink.sinkLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.linkedService, this.context
            );
        }
        if (resource.schemaLinkedService) {
            dataflowSink.schemaLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.schemaLinkedService, this.context
            );
        }
        if (resource.rejectedDataLinkedService) {
            dataflowSink.faultToleranceLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.rejectedDataLinkedService, this.context
            );
        }
        dataflowSink.maxConcurrentConnections = resource.maxConcurrentConnections;
        dataflowSink.description = resource.description;

        if (__IS_FABRIC_MDF__) {
            this._restoreMdfArtifactSinkModel(resource, dataflowSink);
        }

        // Convert to single file option if there's only one patitioned file name with single partition option.
        if (
            dataflowSink.partitionFileNames &&
            dataflowSink.partitionFileNames.length === 1 &&
            dataflowSink.partitionFileNames[0].expression &&
            dataflowSink.partitionOption === 'NoPartition'
        ) {
            dataflowSink.fileNameOption = FileNameOption.SingleFile;
            dataflowSink.singleFile = dataflowSink.partitionFileNames[0];
            dataflowSink.partitionFileNames = [
                Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, dataflowSink)
            ];
        }
        return dataflowSink;
    }

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<never> {
        throw `Artifact type '${tridentResource.artifactType}' does not implement _toAzureResource method.`;
    }

    // ── MDF Fabric artifact sink helpers ───────────────────────────────────

    /** Returns true when the MDF sink is a workspace-built-in Lakehouse or Warehouse. */
    private _isMdfFabricArtifactSink(model: DataFlowSink): boolean {
        return model.isInlineDataset &&
            model.sinkLinkedService?.isWorkspaceBuiltIn === true &&
            (model.sinkLinkedService instanceof LakehouseLinkedService ||
                model.sinkLinkedService instanceof DataWarehouseLinkedService);
    }

    /**
     * Shapes the outgoing sink JSON for Fabric Lakehouse/Warehouse inline-dataset sinks.
     *
     *  - DMTS credential selected: outer `typeProperties` carry artifactId/workspaceId
     *    (+ `endpoint` for Warehouse) and `linkedService` is rewritten to point at the
     *    credential (becomes `externalReferences.connection`).
     *  - "None" (connectionless): the artifact's `type`/`typeProperties` are emitted inline
     *    (matching the connection-based nodes) and `linkedService` is cleared so no
     *    `externalReferences.connection` is emitted.
     *
     * In both cases `schemaLinkedService` is cleared: the schema is already known from the
     * artifact connection itself and emitting it would produce an unwanted `schemaExternalReferences`
     * entry in the MDF sink JSON.
     */
    private async _applyMdfArtifactSinkShape(
        model: DataFlowSink,
        resource: IDataFlowSink
    ): Promise<void> {
        const artifactLs = model.sinkLinkedService as { workspaceId?: string; artifactId?: string; objectId?: string; endpoint?: string };

        if (model.sinkDmtsCredentialLinkedService) {
            this._applyDmtsCredentialShape(model, resource, artifactLs);
        } else {
            await this._applyConnectionlessShape(model, resource);
        }

        // Schema is derived from the artifact itself — suppress the reference so MDF
        // does not emit a spurious `schemaExternalReferences` block.
        resource.schemaLinkedService = undefined;
    }

    /** Writes typeProperties + rewrites linkedService to the DMTS credential reference. */
    private _applyDmtsCredentialShape(
        model: DataFlowSink,
        resource: IDataFlowSink,
        artifactLs: { workspaceId?: string; artifactId?: string; objectId?: string; endpoint?: string }
    ): void {
        // The artifact LS may store the artifact id in either `artifactId` or `objectId` (matches source adapter).
        const artifactId = artifactLs.artifactId ?? artifactLs.objectId;
        if (artifactId) {
            const isWarehouse = model.sinkLinkedService instanceof DataWarehouseLinkedService;
            // Set type as sibling of typeProperties (not inside it)
            resource.type = isWarehouse ? 'DataWarehouse' : 'Lakehouse';
            resource.typeProperties = {
                ...(isWarehouse && artifactLs.endpoint
                    ? { endpoint: artifactLs.endpoint }
                    : {}),
                artifactId,
                workspaceId: artifactLs.workspaceId ?? model.sinkLinkedService.workspaceObjectId,
            };
        }
        resource.linkedService = ReferenceAdapter.toResource(
            model.sinkDmtsCredentialLinkedService,
            this.context
        ) as Model.LinkedServiceReference;
    }

    /**
     * Connectionless ("None") case: emits the artifact's `type`/`typeProperties` inline
     * (matching the connection-based nodes) instead of wrapping them in a `connectionSettings`
     * block. Strips extra typeProperty fields the LS adapter may have written so only the
     * fields MDF expects are present. The backend converter reconstructs the connection
     * settings from these inline fields.
     */
    private async _applyConnectionlessShape(
        model: DataFlowSink,
        resource: IDataFlowSink
    ): Promise<void> {
        const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(model.sinkLinkedService));
        const inlinedLinkedService: Model.LinkedServiceResource = await linkedServiceAdapter.toResource(
            model.sinkLinkedService
        );

        if (inlinedLinkedService?.properties) {
            this._stripExtraTypeProperties(inlinedLinkedService, model.sinkLinkedService instanceof DataWarehouseLinkedService);
        }

        resource.type = inlinedLinkedService.properties?.type;
        resource.typeProperties = inlinedLinkedService.properties?.typeProperties as Record<string, unknown> | undefined;
        resource.linkedService = undefined;
    }

    /**
     * Removes typeProperty keys that MDF does not expect.
     * Warehouse keeps: workspaceId, artifactId, endpoint.
     * Lakehouse keeps: workspaceId, artifactId.
     */
    private _stripExtraTypeProperties(
        linkedServiceResource: Model.LinkedServiceResource,
        isWarehouse: boolean
    ): void {
        const tp = linkedServiceResource.properties?.typeProperties as Record<string, unknown> | undefined;
        if (!tp) { return; }
        const allowedKeys: ReadonlySet<string> = new Set(
            isWarehouse ? ['workspaceId', 'artifactId', 'endpoint'] : ['workspaceId', 'artifactId']
        );
        for (const key of Object.keys(tp)) {
            if (!allowedKeys.has(key)) {
                Reflect.deleteProperty(tp, key);
            }
        }
    }

    /**
     * Reconstructs `sinkLinkedService` and `sinkDmtsCredentialLinkedService` when
     * loading a sink back from JSON. The artifact's `type`/`typeProperties` are inlined on the
     * node; the artifact LS model is rebuilt from them and a DMTS credential, when present, is
     * resolved from `linkedService`/`linkedServiceName`.
     */
    private _restoreMdfArtifactSinkModel(
        resource: IDataFlowSink,
        dataflowSink: DataFlowSink
    ): void {
        if (resource.typeProperties) {
            // typeProperties contains artifactId/workspaceId - create artifact LS from it
            this._restoreFromTypeProperties(resource, dataflowSink);
        }
    }

    /** Restores the artifact LS (and optional DMTS credential) from the inline `typeProperties` shape (artifactId/workspaceId + `endpoint` for Warehouse). */
    private _restoreFromTypeProperties(
        resource: IDataFlowSink,
        dataflowSink: DataFlowSink
    ): void {
        const tp = resource.typeProperties as { workspaceId?: string; artifactId?: string; endpoint?: string; artifactName?: string };
        
        // Determine artifact type: prefer the top-level `type` (always emitted); fall back to
        // endpoint presence only when `type` is absent (Warehouse carries an endpoint, Lakehouse does not).
        const isWarehouse = resource.type != null
            ? resource.type === 'DataWarehouse' || resource.type === 'Warehouse'
            : !!tp.endpoint;
        // Use saved artifact name or fallback to 'artifact'
        const artifactName = tp.artifactName ?? 'artifact';
        const artifactModel = isWarehouse
            ? Metamodel.modelFor(DataWarehouseLinkedService)
            : Metamodel.modelFor(LakehouseLinkedService);
        // Resolve the workspace artifact as a store-backed proxy keyed by its objectId (artifactId) - the
        // SAME identity copy job uses (getResourceIdentifier('linkedService', artifactId) => "linkedservice$$<id>").
        // `sinkLinkedService` is a `reference: true` property, so the model service's fetch-time walker
        // substitutes this marked proxy with the real store entity (the SAME instance as the artifact-list
        // option) - no component-side objectId reconciliation needed. A typed proxy keeps
        // `instanceof Lakehouse/DataWarehouseLinkedService` working before substitution. Fall back to a
        // detached model only when there is no artifactId to resolve by.
        const lsModel: LinkedService = tp.artifactId
            ? ReferenceAdapter.toProxyModel<LinkedService>(artifactModel, { referenceName: tp.artifactId }, this.context)
            : artifactModel.create<LinkedService>(artifactName);
        lsModel.name = artifactName;

        const ls = lsModel as unknown as { workspaceId?: string; artifactId?: string; endpoint?: string };

        // Set tridentArtifactType so isWorkspaceBuiltIn returns true
        lsModel.tridentArtifactType = isWarehouse ? 'DataWarehouse' : 'Lakehouse';

        if (tp.workspaceId) {
            ls.workspaceId = tp.workspaceId;
            lsModel.workspaceObjectId = tp.workspaceId;
        }
        if (tp.artifactId) {
            ls.artifactId = tp.artifactId;
            lsModel.objectId = tp.artifactId;
        }
        if (tp.endpoint) {
            ls.endpoint = tp.endpoint;
        }

        // The DMTS credential reference can be in:
        // 1. `resource.linkedService` (direct reference shape)
        // 2. `resource.linkedServiceName` (parsed from externalReferences.connection during DSL parsing)
        const linkedServiceName = (resource as { linkedServiceName?: { type?: string; referenceName?: string } }).linkedServiceName;

        if (resource.linkedService) {
            const dmtsProxy = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                resource.linkedService,
                this.context
            );
            if (dmtsProxy) {
                const ref = resource.linkedService as { referenceName?: string };
                if (ref.referenceName && !dmtsProxy.objectId) {
                    dmtsProxy.objectId = ref.referenceName;
                }
            }
            dataflowSink.sinkDmtsCredentialLinkedService = dmtsProxy;
        } else if (linkedServiceName?.referenceName) {
            // MDF saves DMTS credential as externalReferences.connection, which gets parsed to linkedServiceName.
            // Resolve as a store-backed proxy (same as copy activity's dataset adapter) so it is the same
            // instance as the connection-list option and needs no component-side objectId reconciliation.
            const dmtsProxy = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                linkedServiceName,
                this.context
            );
            if (dmtsProxy && !dmtsProxy.objectId) {
                dmtsProxy.objectId = linkedServiceName.referenceName;
            }
            dataflowSink.sinkDmtsCredentialLinkedService = dmtsProxy;
        } else {
            // Connectionless: no credential reference present. Clear any stale value so reusing an
            // existing DataFlowSink model in `_toModel` doesn't retain a previously selected credential.
            dataflowSink.sinkDmtsCredentialLinkedService = undefined;
        }
        
        // MDF artifacts are inline datasets - must set this for UI to detect the format override
        dataflowSink.isInlineDataset = true;
        dataflowSink.sinkLinkedService = lsModel;
    }
}
